<script>
import Select2 from "./Select2";
import {useMetaTagJsonContent} from "../Composables/useMetaTagJsonContent";
import {useEventBus} from "../Composables/useEventBus";
import {useEventListener} from '@vueuse/core';

export function textWithVariables(text, variables)
{
    let returnString = text;
    for (const [key, value] of Object.entries(variables)) {
        returnString = returnString.replaceAll('{' + key + '}', value !== null ? value : '');
    }
    return returnString.replaceAll(/\{.*?\}/g, '');
}

export function extractVariables(text, staticVariables) {
    let parts = [];
    parts = text.split('{').filter((value, index) => index > 0);
    parts = parts.concat(staticVariables);
    const partSet = new Set(parts.map(value => value.split('}')[0]).map(variable => variable.trim()));
    return partSet;
}

function updatePositionAndHeight(suggestionList) {
    let viewportHeight = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
    suggestionList.style.maxHeight = '' + (viewportHeight * .35) + 'px';
    if (suggestionList.previousElementSibling === null) {
        return;
    }
    let boundingClientRect = suggestionList.previousElementSibling?.getBoundingClientRect();
    let yPosition = boundingClientRect.top + (boundingClientRect.bottom - boundingClientRect.top) / 2;
    if (yPosition > (viewportHeight / 2)) {
        let computedListHeightString = window.getComputedStyle(suggestionList, null)
            .getPropertyValue('height');
        let computedListHeight = parseInt(computedListHeightString.substr(
            0,
            computedListHeightString.length
        ));
        suggestionList.style.top = '-' + (computedListHeight) + 'px';
        suggestionList.classList.add('above-textarea')
    } else {
        suggestionList.style.top = 'initial';
        suggestionList.classList.remove('above-textarea')
    }
}

export default {
    components: {
        Select2,
    },
    props: {
        modelValue: {
            type: Object,
            required: true,
        },
        text: {
            type: String,
            required: true,
        },
        suggestions: {
            type: Array,
            required: true
        },
        label: {
            type: String,
            default: '',
        },
        variables: {
            type: Object,
            default: {},
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        staticVariableNames: {
            type: Array,
            default: [],
        },
        disabledVariableNames: {
            type: Array,
            default: [],
        },
    },
    data() {
        return {
            showSuggestions: false,
            freeTextKeyword: useMetaTagJsonContent('deficiencyFreeTextKeyword'),
            lastTextWithVariables: '',
        };
    },
    computed: {
        variableNames() {
            return extractVariables(this.text, this.staticVariableNames.concat(...this.disabledVariableNames));
        },
    },
    mounted() {
        this.lastTextWithVariables = textWithVariables(this.text, this.variables);
        useEventBus().on('updateSuggestionTextWithVariables', () => {
            this.$nextTick(() => {
                this.lastTextWithVariables = textWithVariables(this.text, this.variables);
            });
        });
    },
    updated() {
        if (!this.disabled) {
            this.$nextTick(() => {
                let suggestionList = this.$refs.suggestionList;
                if (suggestionList) {
                    updatePositionAndHeight(suggestionList);
                    let listener = () => {
                        updatePositionAndHeight(suggestionList);
                    };
                    useEventListener(window, 'resize', listener);
                }
            });
        }
    },
    methods: {
        suggestionClicked(suggestion) {
            this.$emit('select-suggestion', suggestion);
            this.showSuggestions = false;
        },
        input(event) {
            this.showSuggestions = true;
            let value = event.target.value;
            this.$emit('update:text', value);
        },
        containsCommaAndKeyword(text) {
            return this.containsComma(text) && text.includes(this.freeTextKeyword);
        },
        containsComma(text) {
            return text.includes(',');
        },
        splitOnCommaAndTrim(text) {
            let options = text.split(',').map(split => split.trim());
            if (options.includes(this.freeTextKeyword)) {
                options.splice(options.indexOf(this.freeTextKeyword), 1);
                if (!options.includes(this.modelValue.variables[text])) {
                    options.push(this.modelValue.variables[text]);
                }
            }
            return options;
        },
        getCreateTagCallback(name) {
            return (params) => {
                if (this.containsCommaAndKeyword(name)) {
                    return {
                        id: params.term,
                        text: params.term
                    }
                } else {
                    return null;
                }
            }
        },
        updateLastText()
        {
            this.lastTextWithVariables = textWithVariables(this.text, this.variables);
        },
    },
    emits: [
        'select-suggestion',
        'update:text',
    ],
};
</script>
<template>
    <div v-if="!disabled">
        <label class="form-label">{{ label }}</label>
        <div class="position-relative">
            <div class="input-group">
                <div class="form-group flex-grow-1">
                    <textarea class="form-control" data-testid="deficiencyText" :value="text" :disabled="disabled" @focusin="showSuggestions = true" @input="input"></textarea>
                    <div v-if="showSuggestions" class="position-absolute d-flex flex-column bg-primary shadow-sm suggestion-list last-child-border-bottom-0" ref="suggestionList">
                        <div v-for="suggestion in suggestions" @click="suggestionClicked(suggestion)">
                            <slot name="suggestion" :suggestion="suggestion">{{ suggestion }}</slot>
                        </div>
                    </div>
                </div>
                <div class="input-group-addon btn btn-outline-secondary d-flex align-items-center" @click="updateLastText">
                    <i class="fas fa-sync"></i>
                </div>
                <div class="input-group-addon btn btn-outline-secondary d-flex align-items-center" @click="$emit('update:text', '')">
                    <i class="fas fa-trash-alt"></i>
                </div>
            </div>
        </div>
    </div>
    <div v-if="variableNames.size > 0 || disabled">
        <label v-if="disabled" class="form-label">{{ label }}</label>
        <textarea class="form-control" disabled>{{ lastTextWithVariables }}</textarea>
    </div>
    <div v-for="name in staticVariableNames" v-if="disabled" :key="name">
        <label class="form-label">{{ name }}</label>
        <input class="form-control" v-model="variables[ name ]" autocomplete="off" disabled/>
    </div>
    <div v-for="name in disabledVariableNames" v-if="disabled" :key="name">
        <label class="form-label">{{ name }}</label>
        <input class="form-control" v-model="variables[ name ]" autocomplete="off" disabled/>
    </div>
    <div v-for="name in variableNames" v-if="!disabled" :key="name">
        <label class="form-label">{{ name }}</label>
        <select2 class="form-select" v-model="variables[ name ]" :options="splitOnCommaAndTrim(name)" :settings="{tags: true, allowClear: name.match(new RegExp(freeTextKeyword)) || splitOnCommaAndTrim(name).includes(''), placeholder: ''}" v-if="containsComma(name)" :disabled="disabledVariableNames.includes(name)" :create-tag="getCreateTagCallback(name)" />
        <input class="form-control" v-model="variables[ name ]" autocomplete="off" v-else :disabled="disabledVariableNames.includes(name)" />
    </div>
</template>
